import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { getData, updateData } from "../../hooks/api/utils";
import Loader from "../../components/loader/loader";
import Sidebar from "../sidebar/sidebar";
import { get, post } from "../../hooks/services/service";
import CreatableSelect from "react-select/creatable";
import { FaUpload } from "react-icons/fa";

const EditIndustry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id, industry } = location.state || {};
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    is_active: "true",
    image: null,
    vegetation_type: "",
    soil_type: "",
  });

  const [existingImage, setExistingImage] = useState(null);
  const [imageName, setImageName] = useState(""); 
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  // Fetch Services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await get("admin-panel/services-list/");
        const options = res?.data?.data?.map((s) => ({
          label: s.name,
          value: s.id,
        }));
        setServiceOptions(options);
      } catch (err) {
        console.error("Error fetching services", err);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const populateData = (data) => {
      setFormData({
        name: data.name || "",
        is_active: String(data.is_active),
        image: null,
        vegetation_type: Array.isArray(data.vegetation_type)
          ? data.vegetation_type.join(", ")
          : "",
        soil_type: Array.isArray(data.soil_type)
          ? data.soil_type.join(", ")
          : "",
      });

      setExistingImage(data.image || null);
      setImageName(data.image ? data.image.split("/").pop() : "");

      const selected =
        data.service?.map((s) => ({
          label: s.name,
          value: s.id,
        })) || [];
      setSelectedServices(selected);
      setLoading(false);
    };

    if (industry) {
      populateData(industry);
    } else if (id) {
      getData(
        "/admin-panel/industries/",
        setLoading,
        (data) => populateData(data),
        id
      );
    } else {
      navigate("/industry");
    }
  }, [industry, id, navigate]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      if (file) {
        setImagePreview(URL.createObjectURL(file));
        setImageName(file.name); 
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleServiceChange = async (options) => {
    const updated = [];

    for (let option of options) {
      if (option.__isNew__) {
        try {
          const res = await post("admin-panel/services-list/", {
            name: option.label,
          });
          const newService = { label: res.data.name, value: res.data.id };
          updated.push(newService);
          setServiceOptions((prev) => [...prev, newService]);
        } catch (err) {
          console.error("Error creating service:", err);
        }
      } else {
        updated.push(option);
      }
    }

    setSelectedServices(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = new FormData();
    payload.append("name", formData.name.trim());
    payload.append("is_active", formData.is_active === "true");
    if (formData.image) payload.append("image", formData.image);
    payload.append(
      "vegetation_type",
      JSON.stringify(
        formData.vegetation_type
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v)
      )
    );
    payload.append(
      "soil_type",
      JSON.stringify(
        formData.soil_type
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s)
      )
    );

    selectedServices.forEach((service) => {
      payload.append("service", service.value);
    });

    setIsSubmitting(true);
    await updateData(
      "/admin-panel/industries/",
      id,
      setIsSubmitting,
      payload,
      navigate,
      "/industry",
      true
    );
  };

  return (

      <div className="rightContent">
        <div className="doc_info">
          <div className="row g-4">
            <div className="col-md-12">
              <div className="padding-inner border-radius-20 bg-white">
                <h4 className="mb-4">Edit Industry</h4>
                {errors.api && (
                  <div className="alert alert-danger">{errors.api}</div>
                )}
                {loading ? (
                  <Loader />
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                      {/* Name */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>
                            Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-control ${
                              errors.name ? "is-invalid" : ""
                            }`}
                            placeholder="Enter industry name"
                          />
                          {errors.name && (
                            <div className="invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Status</label>
                          <select
                            name="is_active"
                            value={formData.is_active}
                            onChange={handleChange}
                            className="form-control"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </div>
                      </div>

                      {/* Vegetation */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Vegetation Type (comma-separated)</label>
                          <input
                            type="text"
                            name="vegetation_type"
                            value={formData.vegetation_type}
                            onChange={handleChange}
                            className="form-control"
                            placeholder='e.g. "Wheat, Rice, Maize"'
                          />
                        </div>
                      </div>

                      {/* Soil */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Soil Type (comma-separated)</label>
                          <input
                            type="text"
                            name="soil_type"
                            value={formData.soil_type}
                            onChange={handleChange}
                            className="form-control"
                            placeholder='e.g. "Clay, Sandy, Loamy"'
                          />
                        </div>
                      </div>

                      {/* Services */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Services</label>
                          <CreatableSelect
                            isMulti
                            value={selectedServices}
                            onChange={handleServiceChange}
                            options={serviceOptions}
                            placeholder="Select or type to create services"
                            classNamePrefix="react-select"
                          />
                        </div>
                      </div>

                      {/* Image */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>
                            Upload Image<span className="text-danger">*</span>
                          </label>
                          <div className="custom-file-upload">
                            <input
                              type="file"
                              id="fileInput"
                              hidden
                              name="image"
                              onChange={handleChange}
                            />
                            <label htmlFor="fileInput" className="upload-box">
                              <span>
                                {formData.image
                                  ? formData.image.name
                                  : imageName
                                  ? imageName
                                  : "Upload"}
                              </span>
                              <FaUpload className="upload-icon" />
                            </label>
                          </div>

                          {/* Preview */}
                          {(imagePreview || existingImage) && (
                            <div className="mt-3">
                              <img
                                src={imagePreview || existingImage}
                                alt="Preview"
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "200px",
                                  borderRadius: "10px",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="col-md-12">
                        <div className="d-flex gap-3 justify-content-end">
                          <NavLink
                            to={"/industry"}
                            type="button"
                            className="transparent_btn"
                          >
                            Cancel
                          </NavLink>
                          <button
                            type="submit"
                            className="blue_btn"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Submitting..." : "Update Industry"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default EditIndustry;